import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button } from 'components/_new/Button';
import { chatApi } from 'api/chat';
import { useGlobal } from 'contexts/globalContext';
import { IconSvg } from 'components/_new/Icon';
import { useLocation } from 'react-router-dom';
import { useChat } from './useChat';
import { useChatThreadSubscriber } from './useChatSubscrber';
import { useChatContext } from './ChatContextProvider';

type ChatButtonProps = {
	programPanel?: boolean;
	landingMode?: boolean;
};

export const ChatButton = ({ programPanel = false, landingMode = false }: ChatButtonProps) => {
	const { t } = useTranslation();

	const { currentProgramMembershipId, globalDisabled } = useChatContext();
	const {
		access,
		openDialog,
		dialogProps: { visible },
		queryUniqueParam,
	} = useChat();
	const { newChats, setNewChats } = useGlobal();

	const { refetch: chatApiStatusRefetch, isLoading: statusLoading } = useQuery(
		['chat-status', queryUniqueParam],
		() => chatApi.getStatus(currentProgramMembershipId!),
		{
			enabled: Boolean(currentProgramMembershipId) && access.hasAccess,
			initialData: [],
			refetchOnWindowFocus: true,
			cacheTime: 60000,
			onSuccess: (data) => {
				setNewChats(data);
			},
		}
	);

	const statusRefetchDelayed = () => {
		// if dialog is open, delay refetch for prevent fetch "open & now read" threads
		if (visible) {
			setTimeout(() => {
				if (!statusLoading) {
					chatApiStatusRefetch();
				}
			}, 1000);
		}

		// otherwise refetch now
		else if (!statusLoading) {
			chatApiStatusRefetch();
		}
	};

	const chatThreadHandlerEvent = useCallback(() => {
		statusRefetchDelayed();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useChatThreadSubscriber({
		onChatThread: chatThreadHandlerEvent,
		onChatThreadSelf: chatThreadHandlerEvent,
	});

	const location = useLocation();
	useEffect(() => {
		const currentTitle = document.title.replace(/^\([0-9]+\)\s/g, '');
		if (newChats.length > 0) {
			document.title = `(${newChats.length}) ${currentTitle}`;
		} else {
			document.title = currentTitle;
		}
	}, [location.pathname, newChats.length]);

	if (globalDisabled) {
		return null;
	}

	return (
		<Button
			onClick={() => openDialog()}
			rounded
			variantSize="lg"
			variant={landingMode ? 'on-purple' : 'primary-outlined'}
			icon={() => (
				<IconSvg
					name="contact"
					className="icon-container icon-container-24"
					style={{ transform: 'translateX(1px)' }}
				/>
			)}
			iconOnly
			label={t('chat.title')}
			badge={newChats.length}
			loading={access.loading}
			disabled={!access.hasAccess}
		/>
	);
};
